import React, { useEffect, useState } from "react";
import { getStopListSearchApi } from "../../services/SearchService";
import Select from "react-select";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Icon } from "../../components";
import { Card } from "react-bootstrap";
import Block from "../../components/Block/Block";
import { toast } from "react-toastify";
import { FaTrashCanArrowUp } from "react-icons/fa6";
import {
  getRouteStopSequenceApi,
  createRouteStopSequenceApi,
} from "../../services/TransportService";
import { getRouteApi } from "../../services/TransportService";
import { filter } from "lodash";
const StopSequenceManager = () => {
  const navigate = useNavigate();
  const { route_id } = useParams();

  const [stopSequence, setStopSequence] = useState([]);
  const [availableStops, setAvailableStops] = useState([]);
  const [newStop, setNewStop] = useState(null);
  const [orderNumber, setOrderNumber] = useState("");
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const [showConfirm, setShowConfirm] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [buses, setBuses] = useState([]);
  const [timeDifference, setTimeDifference] = useState("");

  useEffect(() => {
    const fetchRouteStops = async () => {
      setIsLoading(true);
      try {
        // Fetch stop sequence
        const res = await getRouteStopSequenceApi(route_id);
        const { bus_assignments, route_stop_sequences } = res.data;

        // Flatten all schedules into one array
        const allSchedules = route_stop_sequences.flatMap(
          (i) => i.schedules || []
        );

        const getScheduleTimes = (
          route_stop_id = null,
          bus_assigned = null
        ) => {
          const emptyTime = {
            inTime: null,
            outTime: null,
            schedule_id: null,
          };
          if (route_stop_id === null || bus_assigned === null) {
            return emptyTime;
          }
          const schedule = allSchedules.find(
            (s) =>
              s.route_stop === route_stop_id && s.bus_assigned === bus_assigned
          );
          if (!schedule) {
            return emptyTime;
          }
          return {
            inTime: schedule.departure_time,
            outTime: schedule.arrival_time,
            schedule_id: schedule.id,
          };
        };

        const commonBusses = (route_stop_id = null) =>
          bus_assignments.map((data, index) => {
            return {
              id: data?.id,
              bus_number: data?.bus?.number_plate,
              shift: data?.shift,
              ...getScheduleTimes(route_stop_id, data?.id || null),
            };
          });

        setStopSequence(
          route_stop_sequences.map((s, i) => ({
            id: s.id,
            stop: s.stop.id,
            order: i + 1,
            buses: commonBusses(s.id), // Initialize buses as an empty array
          }))
        );
        setBuses(commonBusses());
      } catch (err) {
        console.error("Error fetching data", err);
      }
      try {
        const res = await getStopListSearchApi();
        setAvailableStops(res.data);
      } catch (err) {
        console.error("Error fetching stops", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRouteStops(); // Call fetch function on route_id change
  }, [route_id]); // Trigger effect when `route_id` changes

  const handleAddOrUpdateStop = () => {
    if (!newStop) return;
    const stopId = parseInt(newStop?.value);
    if (!stopId) return;

    const insertIndex = orderNumber
      ? Math.min(Math.max(0, parseInt(orderNumber) - 1), stopSequence.length)
      : stopSequence.length;

    const existingIndex = stopSequence.findIndex((s) => s.stop === stopId);

    const updated = [...stopSequence];

    if (existingIndex >= 0) {
      const [existing] = updated.splice(existingIndex, 1);
      updated.splice(insertIndex, 0, existing);
    } else {
      const newEntry = {
        id: null,
        stop: stopId,
        buses: buses.map((b) => {
          return { ...b, inTime: null, outTime: null, schedule_id: null };
        }),
      };
      updated.splice(insertIndex, 0, newEntry);
    }

    setStopSequence(updated.map((s, i) => ({ ...s, order: i + 1 })));
    setNewStop(null);
    setOrderNumber("");
  };

  const handleRemove = (index) => {
    setShowConfirm(index);
    setConfirmAction(() => () => {
      const updated = [...stopSequence];
      updated.splice(index, 1);
      setStopSequence(updated.map((s, i) => ({ ...s, order: i + 1 })));
      setShowConfirm(null);
    });
  };

  const confirmDrop = () => {
    if (draggedIndex === null || dropIndex === null) return;
    const updated = [...stopSequence];
    const [moved] = updated.splice(draggedIndex, 1);
    updated.splice(dropIndex, 0, moved);
    setStopSequence(updated.map((s, i) => ({ ...s, order: i + 1 })));
    setDraggedIndex(null);
    setDropIndex(null);
    setShowConfirm(null);
  };

  const cancelConfirm = () => {
    setDraggedIndex(null);
    setDropIndex(null);
    setShowConfirm(null);
  };

  const handleDragStart = (index) => setDraggedIndex(index);

  const handleDrop = (index) => {
    if (draggedIndex === null || draggedIndex === index) return;
    setDropIndex(index);
    setShowConfirm(index);
    setConfirmAction(() => confirmDrop);
  };

  const goBack = () => navigate(`/route/${route_id}`);

  const getAvailableStops = () => {
    const selectedIds = stopSequence.map((s) => s.stop.toString());
    return availableStops.filter((s) => !selectedIds.includes(s.value));
  };

  const handleTimeDifferenceChange = (e, stopIndex) => {
    if (e.key === "Enter") {
      const differenceInMinutes = parseInt(e.target.value, 10);
      setTimeDifference("");
      setStopSequence((prevState) => {
        const updatedStopSequence = [...prevState];
        if (stopIndex > 0) {
          const prevStop = updatedStopSequence[stopIndex - 1];
          const currentStop = updatedStopSequence[stopIndex];

          updatedStopSequence[stopIndex] = {
            ...currentStop,
            buses: currentStop.buses.map((bus, index) => {
              const prevBus = prevStop.buses[index];

              let prevInTime = prevBus?.inTime || "00:00:00"; // Default to 00:00:00
              let prevOutTime = prevBus?.outTime || "00:00:00";

              // If time is 'HH:mm:ss', keep only 'HH:mm'
              if (prevInTime.length === 8) {
                prevInTime = prevInTime.slice(0, 5);
              }
              if (prevOutTime.length === 8) {
                prevOutTime = prevOutTime.slice(0, 5);
              }

              const prevInDate = new Date(`1970-01-01T${prevInTime}:00`);
              const prevOutDate = new Date(`1970-01-01T${prevOutTime}:00`);

              // +minutes for inTime
              const newInDate = new Date(prevInDate);
              newInDate.setMinutes(
                newInDate.getMinutes() + differenceInMinutes
              );

              // -minutes for outTime
              const newOutDate = new Date(prevOutDate);
              newOutDate.setMinutes(
                newOutDate.getMinutes() - differenceInMinutes
              );

              const formattedInTime = newInDate.toTimeString().slice(0, 5);
              const formattedOutTime = newOutDate.toTimeString().slice(0, 5);

              return {
                ...bus,
                inTime: !prevInTime.startsWith("00:00") ? formattedInTime : "",
                outTime: !prevOutTime.startsWith("00:00")
                  ? formattedOutTime
                  : "",
              };
            }),
          };
        }

        return updatedStopSequence;
      });
    }
  };

  const handleInTimeChange = (stopId, busId, value) => {
    setStopSequence((prevState) =>
      prevState.map((stop) => {
        if (stop.id === stopId) {
          return {
            ...stop,
            buses: stop.buses.map((bus) => {
              if (bus.id === busId) {
                return {
                  ...bus,
                  inTime: value, // Update inTime for the specific bus
                };
              }
              return bus;
            }),
          };
        }
        return stop;
      })
    );
  };

  // Handle outTime change for a specific bus
  const handleOutTimeChange = (stopId, busId, value) => {
    setStopSequence((prevState) =>
      prevState.map((stop) => {
        if (stop.id === stopId) {
          return {
            ...stop,
            buses: stop.buses.map((bus) => {
              if (bus.id === busId) {
                return {
                  ...bus,
                  outTime: value, // Update outTime for the specific bus
                };
              }
              return bus;
            }),
          };
        }
        return stop;
      })
    );
  };

  const handlesubmit = () => {
    const payload = stopSequence.map((data) => ({
      buses: data?.buses.map((d) => ({
        bus_assignment_id: d.id,
        in_time: d?.inTime || null,
        out_time: d?.outTime || null,
        schedule_id: d.schedule_id || null,
      })), // busId
      id: data.id || null,
      stop: data.stop,
      order: data.order,
    }));

    setIsSubmitLoading(true);
    createRouteStopSequenceApi(route_id, { stops: payload })
      .then((resp) => {
        toast.success(resp?.data?.message || "Stop sequence saved!");
        goBack();
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.error || "Error saving stop sequence."
        );
      })
      .finally(() => setIsSubmitLoading(false));
  };

  const getStop = (stopId) => {
    const stop = availableStops.find((s) => s.value === stopId);
    return stop;
  };
  return (
    <div>
      <Block.Head>
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">Manage Route Stop</Block.Title>
            <nav>
              <ol className="breadcrumb breadcrumb-arrow mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/routes">Routes</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={`/route/${route_id}`}>Route Details</Link>
                </li>
                <li className="breadcrumb-item active">Manage Stop</li>
              </ol>
            </nav>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>

      <Block>
        <Card className="card-gutter-md">
          <Card.Body>
            <ul className="">
              {stopSequence.map((item, index) => {
                return (
                  <li
                    key={index}
                    className={`${draggedIndex === index ? "dragging" : ""}`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(index)}
                  >
                    <div
                      className="card mb-4 rounded-3 flex-fill"
                      // style={{ backgroundColor: "#f9fafb" }}
                    >
                      <div className="d-flex justify-content-between align-items-start px-2 py-3 w-100">
                        <div className="stop-data d-flex gap-2">
                          <span className="stop-pin">
                            <span className="stop-order">{index + 1}</span>
                          </span>
                          <span className="stop-name">
                            {getStop(item.stop.toString())?.label}
                          </span>
                        </div>
                        {index !== 0 && (
                          <div>
                            <input
                              type="number"
                              className="form-control"
                              value={timeDifference}
                              onChange={(e) => {
                                const value = e.target.value;
                                const differenceInMinutes = parseInt(value, 10);
                                if (
                                  !isNaN(differenceInMinutes) &&
                                  differenceInMinutes >= 0
                                ) {
                                  setTimeDifference(differenceInMinutes);
                                } else if (value === "") {
                                  setTimeDifference("");
                                }
                              }} // Pass stopId to the handler
                              onKeyPress={(e) =>
                                handleTimeDifferenceChange(e, index)
                              }
                              placeholder="Enter Time difference in min"
                            />
                          </div>
                        )}

                        {showConfirm === index ? (
                          <div className="confirm-box mt-2">
                            <small>Are you sure to replace the Order?</small>
                            <div className="d-flex gap-2 mt-1">
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => {
                                  if (
                                    draggedIndex !== null &&
                                    dropIndex !== null
                                  ) {
                                    // Drag-drop reorder
                                    const updated = [...stopSequence];
                                    const [moved] = updated.splice(
                                      draggedIndex,
                                      1
                                    );
                                    updated.splice(dropIndex, 0, moved);
                                    setStopSequence(
                                      updated.map((s, i) => ({
                                        ...s,
                                        order: i + 1,
                                      }))
                                    );
                                    setDraggedIndex(null);
                                    setDropIndex(null);
                                    setShowConfirm(null);
                                  } else {
                                    // Remove stop
                                    const updated = [...stopSequence];
                                    updated.splice(showConfirm, 1);
                                    setStopSequence(
                                      updated.map((s, i) => ({
                                        ...s,
                                        order: i + 1,
                                      }))
                                    );
                                    setShowConfirm(null);
                                  }
                                }}
                              >
                                Yes
                              </button>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={cancelConfirm}
                              >
                                No
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="bg-light cursor-pointer"
                            style={{ padding: "0.2% 1%", borderRadius: "5px" }}
                            onClick={() => handleRemove(index)}
                          >
                            <FaTrashCanArrowUp />
                          </div>
                        )}
                      </div>

                      <div className="table-responsive">
                        <table className="table table-borderless mb-0 shadow-sm rounded">
                          <thead className="table-primary">
                            <tr>
                              <th style={{ width: "40%" }}>Bus</th>
                              <th style={{ width: "30%" }}>
                                Pickup (Departure) Time
                              </th>
                              <th style={{ width: "30%" }}>
                                Drop (Arrival) Time
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {item?.buses?.map((bus, busIndex) => (
                              <tr key={busIndex}>
                                <td>
                                  <div className="d-flex flex-column align-items-start">
                                    {/* Bus Number */}
                                    <span
                                      className="text-primary fw-semibold"
                                      style={{ fontSize: "1.1rem" }}
                                    >
                                      {bus.bus_number}
                                    </span>

                                    {/* Shift Name */}
                                    <span
                                      className="text-secondary"
                                      style={{ fontSize: "1rem" }}
                                    >
                                      {bus.shift?.name}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <input
                                    type="time"
                                    className="form-control"
                                    style={{ maxWidth: "165px" }}
                                    value={bus.inTime || ""} // Default to empty string if undefined
                                    onChange={(e) =>
                                      handleInTimeChange(
                                        item.id,
                                        bus.id,
                                        e.target.value
                                      )
                                    }
                                    required
                                  />
                                </td>
                                <td>
                                  <input
                                    type="time"
                                    className="form-control"
                                    style={{ maxWidth: "165px" }}
                                    value={bus.outTime || ""} // Default to empty string if undefined
                                    onChange={(e) =>
                                      handleOutTimeChange(
                                        item.id,
                                        bus.id,
                                        e.target.value
                                      )
                                    }
                                    required
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* <div className="stop-data">
                                            <span className="stop-order">{index + 1}</span>
                                            <span className="stop-name">{stop?.label}</span>
                                        </div>
                    {isConfirming ? (
                      <div className="confirm-box mt-2">
                        <small>Are you sure to replace the Order?</small>
                        <div className="d-flex gap-2 mt-1">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => {
                              if (draggedIndex !== null && dropIndex !== null) {
                                // Drag-drop reorder
                                const updated = [...stopSequence];
                                const [moved] = updated.splice(draggedIndex, 1);
                                updated.splice(dropIndex, 0, moved);
                                setStopSequence(
                                  updated.map((s, i) => ({
                                    ...s,
                                    order: i + 1,
                                  }))
                                );
                                setDraggedIndex(null);
                                setDropIndex(null);
                                setShowConfirm(null);
                              } else {
                                // Remove stop
                                const updated = [...stopSequence];
                                updated.splice(showConfirm, 1);
                                setStopSequence(
                                  updated.map((s, i) => ({
                                    ...s,
                                    order: i + 1,
                                  }))
                                );
                                setShowConfirm(null);
                              }
                            }}
                          >
                            Yes
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={cancelConfirm}
                          >
                            No
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        className="btn btn-icon btn-danger btn-sm"
                        type="button"
                        onClick={() => handleRemove(index)}
                      >
                        <Icon name="trash" />
                      </button>
                    )} */}
                  </li>
                );
              })}
            </ul>

            <div className="d-flex align-items-center justify-content-end gap-3 mt-2">
              <div className="flex-fill">
                <Select
                  onChange={(data) => setNewStop(data)}
                  options={getAvailableStops()}
                  name="stop"
                  value={newStop}
                  placeholder="Select Stop"
                  className="react-select"
                  classNamePrefix="select"
                />
              </div>
              <button
                className="btn btn-secondary"
                onClick={handleAddOrUpdateStop}
                disabled={!getAvailableStops().length}
              >
                Add Stop
              </button>
            </div>
          </Card.Body>
          <Card.Footer>
            <div className="d-flex align-items-center justify-content-center gap-2">
              <button
                className="btn btn-outline-secondary"
                onClick={goBack}
                disabled={isSubmitLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handlesubmit}
                disabled={isSubmitLoading}
              >
                {isSubmitLoading ? "Saving..." : "Save"} Stop Sequence
              </button>
            </div>
          </Card.Footer>
        </Card>
      </Block>
    </div>
  );
};

export default StopSequenceManager;
